import {
  ApiEpBilling,
  IApiEpBillingProduct,
  IApiEpBillingProductPlanProduct,
  IApiEpBillingProductPurchaseRes,
} from "@smp/api";
import { useSnackbar } from "notistack";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMutation, useQueryClient } from "react-query";

import { useEpBillingPaymentProvider } from "@/hooks/use_ep_billing_payment_provider";
import { useEpBillingProductPlanProductList } from "@/hooks/use_ep_billing_product_plan_product";
import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { useContextApp } from "@/screens/app/context/app";

import { IContextDialogPurchase } from "./dialog_purchase.types";

const ContextDialogPurchase = createContext<IContextDialogPurchase>(
  null as unknown as IContextDialogPurchase,
);

export const ProviderDialogPurchase = ({
  children,
  paymentType = "single",
  product,
  productTrial,
  onCreateBill,
  onChangeProduct,
  redirectDisabled,
  successPageQuery,
}: PropsWithChildren<{
  paymentType?: "recurring" | "single";
  product: IApiEpBillingProduct;
  productTrial?: IApiEpBillingProductPlanProduct;
  onCreateBill?: (bill: IApiEpBillingProductPurchaseRes) => void;
  onChangeProduct?: (product: IApiEpBillingProduct) => void;
  redirectDisabled?: boolean;
  successPageQuery?: Record<string, string>;
}>) => {
  const queryClient = useQueryClient();

  const {
    state: { me },
  } = useContextApp();

  const paymentTypeChanged = useRef(false);

  const { paymentProviderList } = useEpBillingPaymentProvider();

  const { failureTranslationGet } = useFailureTranslator();

  const { enqueueSnackbar } = useSnackbar();

  const [areInternalFundsUsed, setAreInternalFundsUsed] =
    useState<IContextDialogPurchase["state"]["areInternalFundsUsed"]>(false);

  const [paymentProviderSelected, setPaymentProviderSelected] =
    useState<IContextDialogPurchase["state"]["paymentProviderSelected"]>(
      undefined,
    );

  const [productSelected, setProductSelected] =
    useState<IContextDialogPurchase["state"]["productSelected"]>(product);

  const [paymentTypeSelected, setPaymentTypeSelected] = useState<
    IContextDialogPurchase["state"]["paymentTypeSelected"]
  >(
    me.billing_policy_id !== "inactive" &&
      productSelected.type === "plan-product"
      ? productSelected.is_recurring_payment_allowed
        ? "recurring"
        : "single"
      : paymentType,
  );

  const {
    isProductPlanProductListLoading,
    productPlanProductList: productList,
  } = useEpBillingProductPlanProductList({
    req: {
      query: {
        billing_policy_id: me.billing_policy_id,
        is_recurring_payment_allowed:
          paymentTypeSelected === "recurring" ? true : undefined,
        is_single_payment_allowed:
          paymentTypeSelected === "single" ? true : undefined,
      },
    },
  });

  const [purchaseBill, setPurchaseBill] =
    useState<IContextDialogPurchase["state"]["purchaseBill"]>(undefined);

  const [purchaseRecurringPayment, setPurchaseRecurringPayment] =
    useState<IContextDialogPurchase["state"]["purchaseRecurringPayment"]>(
      undefined,
    );

  const [purchaseStatus, setPurchaseStatus] =
    useState<IContextDialogPurchase["state"]["purchaseStatus"]>(undefined);

  const [isInvoiceEditing, setIsInvoiceEditing] =
    useState<IContextDialogPurchase["state"]["isInvoiceEditing"]>(false);

  const {
    mutate: mutateFeatureSubscriptionPurchase,
    isLoading: isMutateFeatureSubscriptionPurchaseLoading,
  } = useMutation(ApiEpBilling.Product.FeatureSubscription.purchase);

  const {
    mutate: mutatePlanProductPurchase,
    isLoading: isMutatePlanProductPurchaseLoading,
  } = useMutation(ApiEpBilling.Product.PlanProduct.purchase);

  const {
    mutate: mutatePlanProductTrial,
    isLoading: isMutatePlanProductTrialLoading,
  } = useMutation(ApiEpBilling.Product.PlanProduct.trial);

  const {
    mutate: mutatePostsPackagePurchase,
    isLoading: isMutatePostsPackagePurchaseLoading,
  } = useMutation(ApiEpBilling.Product.PostsPackage.purchase);

  const isPurchasing =
    isMutateFeatureSubscriptionPurchaseLoading ||
    isMutatePlanProductPurchaseLoading ||
    isMutatePlanProductTrialLoading ||
    isMutatePostsPackagePurchaseLoading;

  const isInvoiceEditingToggle = () => {
    setIsInvoiceEditing((previousState) => !previousState);
  };

  const paymentProviderListFiltered =
    paymentProviderList && productSelected.price !== undefined
      ? paymentProviderList.filter((paymentProvider) => {
          if (
            paymentTypeSelected === "recurring" &&
            !paymentProvider.is_support_recurring_payments
          ) {
            return false;
          }
          const currencySupported =
            me.billing_policy_currency &&
            paymentProvider.currencies_supported[me.billing_policy_currency];
          const isSinglePaymentOnly =
            !productSelected.is_recurring_payment_allowed &&
            !!productSelected.is_single_payment_allowed;

          if (
            !currencySupported ||
            (isSinglePaymentOnly && !paymentProvider.is_support_single_payments)
          ) {
            return false;
          } else {
            const min = currencySupported.min || 0;
            const max = currencySupported.max || Number.POSITIVE_INFINITY;
            return productSelected.price >= min && productSelected.price < max;
          }
        })
      : undefined;

  const productListFiltered = productList?.filter((product) => {
    if (
      paymentTypeSelected === "recurring" &&
      !product.is_recurring_payment_allowed
    ) {
      return false;
    }
    if (
      paymentTypeSelected === "single" &&
      !product.is_single_payment_allowed
    ) {
      return false;
    }
    if (
      productSelected.type === "plan-product" &&
      productSelected.plan.id !== product.plan.id
    ) {
      return false;
    }
    return true;
  });

  const areInternalFundsUsedToggle = () =>
    setAreInternalFundsUsed((areInternalFundsUsed) => !areInternalFundsUsed);

  const paymentProviderSelectedChange: IContextDialogPurchase["actions"]["paymentProviderSelectedChange"] =
    (provider) => setPaymentProviderSelected(provider);

  const paymentTypeSelectedChange: IContextDialogPurchase["actions"]["paymentTypeSelectedChange"] =
    (paymentType) => {
      paymentTypeChanged.current = true;
      setPaymentTypeSelected(paymentType);
    };

  const productSelectedChange: IContextDialogPurchase["actions"]["productSelectedChange"] =
    (product) => {
      setProductSelected(product);

      if (!paymentTypeChanged.current) {
        setPaymentTypeSelected(
          product.is_recurring_payment_allowed ? "recurring" : "single",
        );
      }

      if (onChangeProduct) {
        onChangeProduct(product);
      }
    };

  const mutateFunctionGet: (
    product: IApiEpBillingProduct,
  ) =>
    | typeof mutateFeatureSubscriptionPurchase
    | typeof mutatePlanProductPurchase
    | typeof mutatePlanProductTrial
    | typeof mutatePostsPackagePurchase = (product) => {
    if (product.type === "feature-subscription") {
      return mutateFeatureSubscriptionPurchase;
    }
    if (product.type === "plan-product") {
      if (!!productTrial) {
        return mutatePlanProductTrial;
      } else {
        return mutatePlanProductPurchase;
      }
    }
    if (product.type === "posts-package") {
      return mutatePostsPackagePurchase;
    }

    return () => {};
  };

  const purchase = () => {
    const urlOrigin =
      window.location.host === "localhost"
        ? "https://staging.smmplanner.com"
        : window.location.origin;

    if (!paymentProviderSelected) return;

    const mutate = mutateFunctionGet(productSelected);

    let payment_url_success = urlOrigin + "/home/public/purchase/success";

    if (successPageQuery) {
      payment_url_success += `?${new URLSearchParams(
        successPageQuery,
      ).toString()}`;
    }

    mutate(
      {
        body: {
          allow_internal_funds: areInternalFundsUsed,
          payment_provider: paymentProviderSelected.id,
          payment_type: paymentTypeSelected,
          payment_url_cancel: urlOrigin + "/home/public/purchase/cancel",
          payment_url_fail: urlOrigin + "/home/public/purchase/fail",
          payment_url_success,
        },
        path: { id: productSelected.id },
      },
      {
        onError: (clause) => {
          enqueueSnackbar(failureTranslationGet(clause), {
            variant: "error",
          });
        },
        onSuccess: (res) => {
          setPurchaseBill(res.bill);
          setPurchaseRecurringPayment(res.recurring_payment);
          setPurchaseStatus(res.status);
          onCreateBill?.(res);
          queryClient.invalidateQueries("infinite__ep.billing.bill", {
            refetchInactive: true,
          });
        },
      },
    );
  };

  const resetPurchaseStatus = () => setPurchaseStatus(undefined);

  useEffect(() => {
    if (productSelected.type === "plan-product") {
      setPaymentTypeSelected((state) => {
        if (!productSelected.is_recurring_payment_allowed) {
          return "single";
        }

        return state;
      });
    }
  }, [productSelected]);

  /**
   * here we need to disable internal funds using
   * if recurring payment is enabled
   */
  useEffect(() => {
    if (paymentTypeSelected === "recurring") {
      setAreInternalFundsUsed(false);
    }
  }, [paymentTypeSelected]);

  /**
   * here we need to replace product if it does
   * not exist on filtered product list
   */
  useEffect(() => {
    if (
      !productListFiltered?.length ||
      productSelected.type !== "plan-product"
    ) {
      return;
    }

    const match =
      productListFiltered.findIndex(
        (product) => product.id === productSelected.id,
      ) >= 0;

    if (!match) {
      const newProduct = [...productListFiltered].sort(
        (pp1, pp2) =>
          pp2.price -
          productSelected.price -
          (pp1.price - productSelected.price),
      )[0];

      setProductSelected(newProduct);
      setPaymentTypeSelected(
        newProduct.is_recurring_payment_allowed ? "recurring" : "single",
      );
    }
  }, [product, productListFiltered, productSelected]);

  /**
   * here we need to replace product with upgrade product
   * if it is trial product
   */
  useEffect(() => {
    if (
      product.type === "plan-product" &&
      product.plan.id === "starter_2015_trial_classic"
    ) {
      const productUpgrade = productList?.find(
        (product) => product.plan.id === product.plan.upgrade,
      );
      if (productUpgrade) {
        setProductSelected(productUpgrade);
      }
    }
  }, [product, productList]);

  /**
   * here we select some provider from list (if available) by default
   */
  useEffect(() => {
    setPaymentProviderSelected((paymentProviderSelected) => {
      if (paymentProviderListFiltered) {
        const cpPaymentProvider = paymentProviderListFiltered.find(
          (paymentProvider) => paymentProvider.id === "cloudpayments",
        );
        if (paymentProviderSelected) {
          const isSupported = paymentProviderListFiltered
            ? paymentProviderListFiltered.find(
                (paymentProvider) =>
                  paymentProvider.id === paymentProviderSelected.id,
              )
            : false;
          if (isSupported) {
            return paymentProviderSelected;
          } else {
            return (
              cpPaymentProvider || paymentProviderListFiltered[0] || undefined
            );
          }
        } else {
          return (
            cpPaymentProvider || paymentProviderListFiltered[0] || undefined
          );
        }
      } else {
        return undefined;
      }
    });
  }, [paymentProviderListFiltered]);

  return (
    <ContextDialogPurchase.Provider
      value={{
        actions: {
          areInternalFundsUsedToggle,
          isInvoiceEditingToggle,
          paymentProviderSelectedChange,
          paymentTypeSelectedChange,
          productSelectedChange,
          purchase,
          resetPurchaseStatus,
        },
        state: {
          areInternalFundsUsed,
          isInvoiceEditing,
          isLoading: isProductPlanProductListLoading,
          isPurchasing,
          paymentProviderList: paymentProviderListFiltered,
          paymentProviderSelected,
          paymentTypeSelected,
          productList: productListFiltered,
          productSelected,
          productTrial,
          purchaseBill,
          purchaseRecurringPayment,
          purchaseStatus,
          redirectDisabled,
        },
      }}
    >
      {children}
    </ContextDialogPurchase.Provider>
  );
};

export const useContextDialogPurchase = () => useContext(ContextDialogPurchase);
